/**
 * Meta Pixel (browser) — complementa CAPI server-side no vendas-ia.
 */

const PIXEL_ID = import.meta.env.VITE_META_PIXEL_ID?.trim() ?? '';
const FBC_STORAGE_KEY = 'lunins_meta_fbc_v1';

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
    _fbq?: (...args: unknown[]) => void;
  }
}

function isEnabled(): boolean {
  return Boolean(PIXEL_ID) && typeof window !== 'undefined';
}

function readCookie(name: string): string | undefined {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : undefined;
}

function writeCookie(name: string, value: string, maxAgeSec = 90 * 24 * 3600): void {
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAgeSec}; SameSite=Lax`;
}

/** Captura fbclid da URL e persiste _fbc (formato Meta). */
export function captureFbclidFromUrl(): void {
  if (typeof window === 'undefined') return;
  const fbclid = new URLSearchParams(window.location.search).get('fbclid');
  if (!fbclid) return;
  const fbc = `fb.1.${Date.now()}.${fbclid}`;
  writeCookie('_fbc', fbc);
  window.localStorage.setItem(FBC_STORAGE_KEY, fbc);
}

export function getMetaBrowserIds(): { fbp?: string; fbc?: string } {
  if (typeof window === 'undefined') return {};
  const fbp = readCookie('_fbp');
  const fbc = readCookie('_fbc') || window.localStorage.getItem(FBC_STORAGE_KEY) || undefined;
  return { fbp, fbc };
}

export function initMetaPixel(): void {
  if (!isEnabled() || window.fbq) return;

  captureFbclidFromUrl();

  const n = window;
  const fbq = function (...args: unknown[]) {
    if (fbq.callMethod) {
      fbq.callMethod(...args);
    } else {
      fbq.queue.push(args);
    }
  } as Window['fbq'] & { callMethod?: (...args: unknown[]) => void; queue: unknown[] };
  fbq.queue = [];
  n.fbq = fbq;
  if (!n._fbq) n._fbq = fbq;

  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  document.head.appendChild(script);

  n.fbq('init', PIXEL_ID);
}

export type MetaPixelEvent = 'ViewContent' | 'Contact' | 'InitiateCheckout' | 'Lead';

export function pixelTrack(
  event: MetaPixelEvent,
  params?: Record<string, string | number | boolean | undefined>,
): void {
  if (!isEnabled() || !window.fbq) return;
  window.fbq('track', event, params ?? {});
}

export function currentUtmParams(): Record<string, string | undefined> {
  if (typeof window === 'undefined') return {};
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get('utm_source') || undefined,
    utm_medium: params.get('utm_medium') || undefined,
    utm_campaign: params.get('utm_campaign') || undefined,
    utm_content: params.get('utm_content') || undefined,
    utm_term: params.get('utm_term') || undefined,
  };
}

/** Monta wa.me com texto pré-preenchido + UTMs para atribuição CRM. */
export function buildWhatsappHref(e164: string, prefillText: string): string {
  const utm = currentUtmParams();
  const params = new URLSearchParams();
  params.set('text', prefillText);
  if (utm.utm_source) params.set('utm_source', utm.utm_source);
  if (utm.utm_medium) params.set('utm_medium', utm.utm_medium);
  if (utm.utm_campaign) params.set('utm_campaign', utm.utm_campaign);
  if (utm.utm_content) params.set('utm_content', utm.utm_content);
  if (utm.utm_term) params.set('utm_term', utm.utm_term);
  if (!utm.utm_source) params.set('utm_source', 'pagina_cinta');
  if (!utm.utm_medium) params.set('utm_medium', 'website');
  return `https://wa.me/${e164}?${params.toString()}`;
}
